import moment from "moment";
import React, { useContext, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Spinner from "../../components/BootstrapCompo";
import DownloadFileModal from "../../components/common/download-file-modal";
import PaginationLayout from "../../components/PaginationLayout";
import { INSUFFICIENT_PERMISSIONS } from "../../context/actionTypes";
import { GlobalContext, handleError } from "../../context/Provider";
import henceforthApi from "../../utils/henceforthApi";

interface StoreListResponse {
    data: {
        total_count: number;
        data?: any[];
    };
}

export default ({ masterSellerId }: { masterSellerId: string }) => {
    const { authState } = useContext(GlobalContext);
    const navigate = useNavigate();
    const location = useLocation();
    const limit = 10;

    const [state, setState] = useState<StoreListResponse>({
        data: { total_count: 0 },
    });
    const [page, setPage] = useState<number>(1);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");

    const newParam = new URLSearchParams(location.search);

    // Helper to build query string for API
    const buildQuery = () => {
        const params = new URLSearchParams();
        if (searchTerm) params.set("search", searchTerm);
        if (statusFilter) params.set("filter", statusFilter);
        if (startDate) params.set("start_date", startDate);
        if (endDate) params.set("end_date", endDate);
        params.set("master_seller_id", masterSellerId);
        return params.toString();
    };

    const fetchStores = async (pageNum: number = page) => {
        setLoading(true);
        try {
            const query = buildQuery();
            const apiRes = await henceforthApi.Seller.getStoreList(
                searchTerm || undefined,
                statusFilter || undefined,
                pageNum,
                limit,
                masterSellerId
            );
            setState(apiRes);
            setPage(pageNum);
        } catch (error: any) {
            if (error?.response?.body?.error === INSUFFICIENT_PERMISSIONS) {
                window.history.back();
            } else {
                handleError(error);
            }
        } finally {
            setLoading(false);
        }
    };

    // Reset to page 1 when filters change
    React.useEffect(() => {
        if (page !== 1) setPage(1);
        fetchStores(1);
    }, [searchTerm, statusFilter, startDate, endDate, authState?.lang]);

    // Initial load
    React.useEffect(() => {
        fetchStores(1);
    }, []);

    const handlePageChange = (newPage: number) => {
        if (newPage < 1 || newPage > Math.ceil(state.data.total_count / limit)) return;
        fetchStores(newPage);
    };


    const totalPages = Math.ceil(state.data.total_count / limit);

    return (
        <div className="order-detail-box">
            {/* Filters Row */}
            <div className="common-card mb-4 border-0 card-spacing">
                <div className="row justify-content-between gy-3">
                    {/* Search */}
                    <div className="col-md-12 col-lg-6">
                        <div className="row gy-3">
                            <div className="col-md-8">
                                <div className="form-fields-box">
                                    <label className="mb-1 form-label fw-semibold">Search</label>
                                    <div className="position-relative">
                                        <input
                                            type="search"
                                            name="search"
                                            className="form-control rounded-0 ps-4"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            placeholder="Search by name, email, phone, address..."
                                        />
                                        <span className="search-icon">
                                            <i className="fa fa-search"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* Store List Table */}
            <div className="common-card">
                <div className="common-card-title">
                    <h5>Stores Under This Seller</h5>
                    <span className="badge bg-theme">{state.data.total_count} Stores</span>
                </div>
                <div className="common-card-content">
                    <div className="data-list-table table-responsive mb-3">
                        {loading ? (
                            <div className="vh-100 d-flex justify-content-center py-5">
                                <Spinner />
                            </div>
                        ) : state.data.data?.length === 0 ? (
                            <div className="text-center py-4">
                                <i className="fa fa-store fa-3x d-block mb-3 text-muted"></i>
                                <p className="text-muted">No stores found.</p>
                            </div>
                        ) : (
                            <table className="table table-striped align-middle">
                                <thead>
                                    <tr>
                                        <th>Sr No.</th>
                                        <th>Store Name</th>
                                        <th>Email</th>
                                        <th>Phone</th>
                                        <th>Company</th>
                                        <th>Address</th>
                                        <th>City</th>
                                        <th>State</th>
                                        <th>Status</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {state.data.data?.map((store: any, index: number) => (
                                        <tr key={store._id}>
                                            <td>{index + 1 + (page - 1) * limit}</td>
                                            <td>{store.name || "-"}</td>
                                            <td>{store.email || "-"}</td>
                                            <td>{store.phone_number || "-"}</td>
                                            <td>{store.company || "-"}</td>
                                            <td>{store.full_address || "-"}</td>
                                            <td>{store.city || "-"}</td>
                                            <td>{store.state || "-"}</td>
                                            <td>
                                                <span
                                                    className={`badge ${store.account_status === "ACTIVATED"
                                                        ? "bg-success"
                                                        : store.is_blocked
                                                            ? "bg-danger"
                                                            : "bg-warning"
                                                        }`}
                                                >
                                                    {store.is_blocked
                                                        ? "BLOCKED"
                                                        : store.account_status || "INACTIVE"}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="btn-group gap-2">
                                                    <button
                                                        className="btn btn-white btn-sm"
                                                        onClick={() => {
                                                            window.location.href = `/seller/${store._id}`;
                                                        }}
                                                    >
                                                        <i className="fa fa-eye me-1"></i>View
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Pagination */}
                    {state.data.total_count > 0 && (
                        <div className="dashboad-pagination-box">
                            <PaginationLayout
                                count={state.data.total_count}
                                data={state.data.data}
                                page={page}
                                limit={Number(limit)}
                                onPageChange={(newPage: number) => handlePageChange(newPage)}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};